
import { Component, Mixins, Ref } from 'vue-property-decorator'
import FormContainer from "@/components/processing_steps/commons/forms/FormContainer.vue"
import StepContainer from "@/components/processing_steps/commons/StepContainer.vue"
import ResultReportForm from "@/components/processing_steps/project/forms/ResultReportForm.vue"
import ResultReportSummaryMixin from "@/components/processing_steps/result_report/mixins/ResultReportSummaryMixin.vue"
import ResultReportStepMixin from "@/components/processing_steps/result_report/mixins/ResultReportStepMixin.vue"
import ProcessingStepMixin from "@/components/processing_steps/ProcessingStepMixin.vue"
import {
  IProcessingStep,
  IPSSelectOption
} from "@/types/processingSteps"
import ResultReportBaseInformationForm
  from "@/components/processing_steps/result_report/forms/ResultReportBaseInformationForm.vue"
import axios from "axios"
import { API_URLS } from "@/utils/helpers"
import WhatWeAchieved from "@/components/processing_steps/result_report/forms/WhatWeAchieved.vue"
import { IProject } from "@/types/projects"
import ContactPersonSelect from '@/components/processing_steps/commons/forms/ContactPersonSelect.vue'
import PublishResultReportForm from "@/components/processing_steps/result_report/forms/PublishResultReportForm.vue"
import FinanceForm from "@/components/processing_steps/result_report/forms/FinanceForm.vue"
import FinanceTable from "@/components/processing_steps/commons/fields/FinanceTable.vue"
import FormTitle from "@/components/processing_steps/FormTitle.vue"
import RemainingFundsUsage from './forms/RemainingFundsUsage.vue'

@Component({
  components: {
    FormTitle,
    FinanceTable,
    FinanceForm,
    ContactPersonSelect,
    PublishResultReportForm,
    WhatWeAchieved,
    ResultReportBaseInformationForm,
    ResultReportForm,
    StepContainer,
    FormContainer,
    RemainingFundsUsage
  }
})
// todo move to processing_steps/result_report
export default class EditResultReport extends Mixins(
  ProcessingStepMixin,
  ResultReportStepMixin,
  ResultReportSummaryMixin) {
  @Ref() contactPersonSelect!: ContactPersonSelect
  resultReportSlug = ""
  isCreate = false
  uploadConfirmation = false
  fundUsageConfirmation = false
  availableProjects: IPSSelectOption[] = []

  baseInfoFormErrors = {
    title: ""
  }

  whatWeAchievedFormErrors = {
    text: "",
    activity_results: "",
    goal_achieved: "",
    page_heading: ""
  }

  get isDraft (): boolean {
    return this.resultReport.status === 1
  }

  get financeStepValid (): boolean {
    if (!this.resultReport?.in_foreign_country) {
      return true
    }

    if (this.resultReport.financing_need_report_items.length === 0) {
      return false
    }

    const noDerivation = !this.financeTableDeviation
    if (noDerivation) {
      return true
    }

    if (this.resultReport.excess_funds_handling === 0) {
      return false
    }

    if (this.resultReport.excess_funds_handling === 1 &&
        this.resultReport.excess_funds_purposes.length > 0) {
      return true
    }

    if (this.resultReport.excess_funds_handling === 2) {
      return true
    }

    return false
  }

  get financeTableDeviation (): number {
    // todo use the one from FinanceTable.vue, but we ref that anyway
    if (!this.project || !this.resultReport?.financing_need_report_items) {
      return 0
    }
    const detailedFinancingNeed = this.project.detailed_financing_need
    const sums = { 1: 0, 2: 0, 3: 0, 4: 0 }
    for (const item of this.resultReport.financing_need_report_items) {
      if (Object.prototype.hasOwnProperty.call(sums, item.expense_type)) {
        sums[item.expense_type] += item.expense_amount
      }
    }
    const deviations = [
      sums[1] - (parseInt(detailedFinancingNeed.investments.need_in_cents) / 100),
      sums[2] - (parseInt(detailedFinancingNeed.operating_costs.need_in_cents) / 100),
      sums[3] - (parseInt(detailedFinancingNeed.staff_costs.need_in_cents) / 100),
      sums[4] - (parseInt(detailedFinancingNeed.other_costs.need_in_cents) / 100)
    ]
    const totalDeviation = deviations.reduce((a, b) => a + b, 0)
    return totalDeviation
  }

  get inForeignCountry (): boolean {
    return this.resultReport?.in_foreign_country
  }

  async mounted(): Promise<void> {
    this.isCreate = window.location.pathname.endsWith("create/")
    await this.fetchAvailableProjects()
    this.setResultReportSlug()
    await this.fetchResultReport()
    await this.fetchProject()
    this.updateBreadCrumbs()
    this.setSteps()
    this.setActiveStep(this.firstStep)
    this.checkAndValidateSteps()
  }

  setResultReportSlug(): void {
    this.resultReportSlug = window.location.href.split(this.urlPrefix() + "result-report/")[1].split("/update/")[0]
  }

  urlPrefix () {
    if (window.location.pathname.split('/').filter(Boolean)[0] === "site-admin") {
      return "/site-admin/"
    }
    return "/account/"
  }

  gotoStep (step: IProcessingStep): void {
    this.setActiveStep(step)
  }

  async fetchAvailableProjects (): Promise<void> {
    const url = this.isSiteAdmin ? API_URLS.PROJECTS.MINIMAL_LIST : API_URLS.PROJECTS.ACCOUNT_LIST + "?own=True"
    await axios.get(url).then((response) => {
      const projectData = this.isSiteAdmin ? response.data : response.data.results
      this.availableProjects = this.isSiteAdmin ? response.data : response.data.results
      this.availableProjects = projectData.map((project: IProject) => ({
        value: project.slug,
        text: project.title
      }))
    }).catch(() => {
      this.showError(null)
    })
  }

  async onPublicContactPersonChanged (): Promise<void> {
    await this.fetchResultReport()
    this.checkAndValidateStep(this.activeStep)
  }

  async saveBtnClicked (): Promise<boolean> {
    let saved = false
    if (this.activeStep.key === "basic") {
      saved = await this.saveResultReport()
    } else if (this.activeStep.key === "what_we_achieved") {
      saved = await this.saveWhatWeAchieved()
    } else if (this.activeStep.key === "contact_persons") {
      saved = this.saveContactPerson()
    } else if (this.activeStep.key === "finance") {
      if (!this.inForeignCountry) {
        saved = true
      } else {
        saved = await this.saveExceededFundsUsage()
      }
    }
    this.checkAndValidateStep(this.activeStep)
    return saved
  }

  saveContactPerson (): boolean {
    if (!this.contactPersonIsValid) {
      this.showAddContactPersonError()
      return false
    }
    this.showSuccessMsg(null)
    return true
  }

  async saveExceededFundsUsage (): Promise<boolean> {
    if (this.resultReport.financing_need_report_items.length === 0) {
      this.showError({
        title: this.$gettext("No financing need items added."),
        message: this.$gettext("Please add financing need items before saving the remaining funds usage.")
      })
      return false
    }
    if (this.resultReport.excess_funds_handling === 0) {
      this.showError({
        title: this.$gettext("Please select how to handle the remaining funds."),
        message: this.$gettext("You have a negative deviation in your financing needs. Please select how you would like to handle the remaining funds before saving.")
      })
      return false
    } else if (this.resultReport.excess_funds_handling === 1 &&
               this.resultReport.excess_funds_purposes.length === 0) {
      this.showError({
        title: this.$gettext("Please select statutory purposes for the remaining funds."),
        message: this.$gettext("You have chosen to allocate the remaining funds to statutory purposes. Please select at least one purpose before saving.")
      })
      return false
    }
    let saved = false
    const url = API_URLS.RESULT_REPORT.SAVE_EXCEEDED_FUNDS_USAGE(this.resultReport.slug)
    const postData = {
      excess_funds_handling: this.resultReport.excess_funds_handling,
      excess_funds_purposes: this.resultReport.excess_funds_purposes.map(p => typeof p === 'object' ? p.id : p)
    }
    await axios.put(url, postData)
      .then(async () => {
        this.showSuccessMsg(null)
        saved = true
        await this.fetchResultReport()
        this.checkAndValidateStep(this.activeStep)
      }).catch((error) => {
        this.showError(error)
      })
    return saved
  }

  async saveAndNextBtnClicked (): Promise<void> {
    const saved = await this.saveBtnClicked()
    if (saved) {
      this.gotoNextStepOrFinish(this.steps, this.activeStep, "result_report")
    }
  }

  async finishBtnClicked () {
    let saved = false
    const allStepsAreValid = this.allStepsValid()
    if (!allStepsAreValid) {
      const incompleteSteps = this.steps.filter(step => !step.completed)
      if (incompleteSteps.length > 0) {
        this.setActiveStep(incompleteSteps[0])
      }
      const title = this.$gettext("Please fill out the required fields")
      const message = incompleteSteps.map(step => step.title).join(", ")
      this.showError({ title, message })
      return
    }
    if (!this.fundUsageConfirmation || !this.uploadConfirmation) {
      this.showTermsError()
      return
    }
    saved = await this.publishResultReport()
    if (saved) {
      this.showSuccessMsg(null)
      this.redirectToTable()
    }
  }

  getCompletionUrl(slug: string) {
    if (this.isSiteAdmin) {
      return "/api/v4/result-reports/" + slug + "/?publish"
    } else {
      return "/api/v4/result-reports/" + slug + "/?own&publish"
    }
  }

  redirectToTable (): void {
    window.location.href = this.tableUrl
  }

  get tableUrl (): string {
    return this.isSiteAdmin ? "/site-admin/result-report/" : "/account/result-report/"
  }

  async onFilesUploaded(): Promise<void> {
    const previousOrder = this.resultReport.financing_need_report_items.map(item => item.id)
    await this.fetchResultReport()
    this.resultReport.financing_need_report_items.sort((a, b) => {
      const indexA = previousOrder.indexOf(a.id)
      const indexB = previousOrder.indexOf(b.id)
      if (indexA === -1 && indexB === -1) return 0
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }

  async fetchResultReport(): Promise<void> {
    const url = this.isSiteAdmin
      ? API_URLS.RESULT_REPORT.RETRIEVE(this.resultReportSlug)
      : API_URLS.RESULT_REPORT.RETRIEVE(this.resultReportSlug) + "?own=True"
    try {
      const response = await axios.get(url)
      this.resultReport = response.data
      this.resultReport.project_slug = response.data.project.slug
    } catch {
      this.showError(null)
    }
  }

  checkAndValidateSteps (): void {
    this.steps.forEach((step: IProcessingStep) => {
      this.checkAndValidateStep(step)
    })
  }

  // todo why return boolean?
  checkAndValidateStep(step: IProcessingStep): boolean {
    let saved = false
    if (!step.required) {
      this.setAdditionalStepValid(step)
      return true
    }

    if (step.key === "basic") {
      if (this.baseInformationIsValid) {
        this.setAdditionalStepValid(step)
        saved = true
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === "what_we_achieved") {
      if (this.whatWeAchievedIsValid) {
        this.setAdditionalStepValid(step)
        saved = true
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === 'finance') {
      if (this.financeStepValid) {
        this.setAdditionalStepValid(step)
        saved = true
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === "contact_persons") {
      if (this.contactPersonIsValid) {
        this.setAdditionalStepValid(step)
        saved = true
      } else {
        this.setAdditionalStepInvalid(step)
      }
    }
    return saved
  }

  updateBreadCrumbs() {
    const tableUrl = "/account/result-report/"
    const createUrl = ""
    let breadcrumbs = []
    const resultReportIcon = "resultReportIcon"
    if (this.isCreate) {
      breadcrumbs = [
        { title: this.$gettext("Result reports"), url: tableUrl, icon: resultReportIcon },
        { title: this.$gettext("Create"), url: createUrl }
      ]
    } else {
      breadcrumbs = [
        { title: this.$gettext("Result reports"), url: tableUrl, icon: resultReportIcon },
        { title: this.resultReport.title, url: '/processing-steps/' }
      ]
    }
    this.$emit("breadcrumbs-set", breadcrumbs) // emits to ProcessingSteps.vue to set the new breadcrumbs
  }

  async fetchProject(): Promise<void> {
    const url = this.isSiteAdmin
      ? API_URLS.PROJECTS.EDIT_DATA(this.resultReport.project_slug)
      : API_URLS.PROJECTS.EDIT_DATA(this.resultReport.project_slug) + "?own=True"
    try {
      const res = await axios.get(url)
      this.project = res.data
    } catch {
      this.showError(null)
    }
  }

  async resetFormErrors (): Promise<void> {
    this.whatWeAchievedFormErrors = {
      text: "",
      activity_results: "",
      goal_achieved: "",
      page_heading: ""
    }
    this.baseInfoFormErrors = {
      title: ""
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async setBaseFormErrors (error: any): Promise<void> {
    // reset errors
    await this.resetFormErrors()
    const errorMessages = error.response.data
    this.baseInfoFormErrors = {
      title: errorMessages.title ? errorMessages.title[0] : ""
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async setWhatWeAchievedFormErrors (error: any): Promise<void> {
    await this.resetFormErrors()
    const errorMessages = error.response.data
    this.whatWeAchievedFormErrors = {
      text: errorMessages.text ? errorMessages.text[0] : "",
      activity_results: errorMessages.activity_results ? errorMessages.activity_results[0] : "",
      goal_achieved: errorMessages.goal_achieved ? errorMessages.goal_achieved[0] : "",
      page_heading: errorMessages.page_heading ? errorMessages.page_heading[0] : ""
    }
  }

  async saveResultReport (): Promise<boolean> {
    let saved = false
    const url = API_URLS.RESULT_REPORT.RETRIEVE(this.resultReport.slug)
    const postData = {
      title: this.resultReport.title,
      project: this.resultReport.project_slug,
      goal_achieved: this.resultReport.goal_achieved,
      text: this.resultReport.text,
      activity_results: this.resultReport.activity_results,
      status: this.resultReport.status,
      review_status: this.resultReport.review_status,
      page_heading: this.resultReport.page_heading
    }
    await axios.put(url, postData)
      .then(async () => {
        this.showSuccessMsg(null)
        saved = true
        await this.fetchResultReport()
        this.checkAndValidateStep(this.activeStep)
      }).catch(async (error) => {
        await this.setBaseFormErrors(error)
        this.showError(null)
      })
    return saved
  }

  async createResultReport (): Promise<boolean> {
    let created = false
    const postData = {
      title: this.resultReport.title,
      project: this.resultReport.project_slug,
      status: 1,
      goal_achieved: false,
      text: "",
      activity_results: "",
      financing_need_report_items: [],
      review_status: 1,
      slug: "",
      links: [],
      is_public: false,
      contact_person: null,
      photo_album_slug: "",
      file_album_slug: "",
      photos: [],
      files: []
    }
    let url = "/api/v4/result-reports/?own"
    if (this.isSiteAdmin) {
      url = "/api/v4/result-reports/"
    }
    created = await axios.post(url, postData).then(response => {
      this.resultReportSlug = response.data.slug
      return true
    }).catch(error => {
      this.showError(error)
      return false
    })
    return created
  }

  get hasRequiredImages (): boolean {
    return this.resultReport.photos.length > 0
  }

  async saveWhatWeAchieved (): Promise<boolean> {
    if (!this.hasRequiredImages) {
      this.showMissingImageError()
      return false
    }
    let saved = false
    const url = API_URLS.RESULT_REPORT.SAVE_WHAT_WE_ACHIEVED(this.resultReport.slug)
    const postData = {
      goal_achieved: this.resultReport.goal_achieved,
      text: this.resultReport.text,
      activity_results: this.resultReport.activity_results,
      page_heading: this.resultReport.page_heading
    }
    await axios.put(url, postData)
      .then(async () => {
        this.showSuccessMsg(null)
        saved = true
        await this.fetchResultReport()
      }).catch((error) => {
        this.setWhatWeAchievedFormErrors(error)
        this.showError(null)
      })
    return saved
  }

  async publishResultReport (): Promise<boolean> {
    let saved = false
    const url = API_URLS.RESULT_REPORT.PUBLISH(this.resultReport.slug)
    const data = {
      terms: this.fundUsageConfirmation && this.uploadConfirmation,
      isInForeignCountry: this.resultReport.in_foreign_country
    }
    saved = await axios.put(url, data).then(async () => {
      return true
    })
    return saved
  }

  get baseInformationIsValid (): boolean {
    return !!this.resultReport.title && !!this.resultReport.project_slug
  }

  get whatWeAchievedIsValid () {
    if (this.resultReport.in_foreign_country) {
      return this.resultReport.activity_results.length > 0 && this.resultReport.text.length > 0
    }
    return this.resultReport.text.length > 0
  }

  get contactPersonIsValid (): boolean {
    return !!this.resultReport.public_contact_person || false
  }

  openAddressBook (): void {
    this.contactPersonSelect.openAddressBook()
  }
}
